import { useMemo, useState } from "react";
import {
  FEATURE_STATUS_META,
  ProductFeature,
  ProductFeatureStatus,
  getFeatureStatusMeta,
} from "../../../api/featureRequests";
import {
  useDeleteFeatureMutation,
  useFeatureRequestsQuery,
  useMergeFeatureMutation,
  useProductFeaturesQuery,
  useUndeployFeatureMutation,
  useUpdateFeatureMutation,
} from "../hooks/useFeatureRequestsQueries";
import { errorMessage, formatDate } from "../utils";
import AddRequestModal from "./AddRequestModal";
import DeployFeatureDialog from "./DeployFeatureDialog";
import PickFeatureDialog from "./PickFeatureDialog";
import RequestsTable from "./RequestsTable";
import ConfirmationModal from "../../ConfirmationModal";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSTextField from "../../system-design/DSTextField";
import DSEnumSelect from "../../system-design/DSEnumSelect";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSErrorButton from "../../system-design/DSErrorButton";
import DSAlert from "../../system-design/DSAlert";
import DSBox from "../../system-design/DSBox";
import DSChip from "../../system-design/DSChip";
import DSTypography from "../../system-design/DSTypography";
import DSCircularProgress from "../../system-design/DSCircularProgress";

const STATUS_OPTIONS = Object.values(ProductFeatureStatus).map((status) => ({
  value: status,
  label: FEATURE_STATUS_META[status].label,
}));

const FeatureEditor = (props: { feature: ProductFeature }) => {
  const update = useUpdateFeatureMutation();
  const [title, setTitle] = useState(props.feature.title);
  const [description, setDescription] = useState(
    props.feature.description ?? "",
  );
  const [status, setStatus] = useState(props.feature.status);

  const dirty =
    title.trim() !== props.feature.title ||
    (description.trim() || null) !== props.feature.description ||
    status !== props.feature.status;

  return (
    <DSBox sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <DSBox sx={{ display: "flex", gap: 2 }}>
        <DSTextField
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ flex: 1 }}
        />
        <DSEnumSelect<ProductFeatureStatus>
          value={status}
          options={STATUS_OPTIONS}
          onChange={setStatus}
          sx={{ minWidth: 160 }}
        />
      </DSBox>
      <DSTextField
        label="Description"
        multiline
        minRows={2}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      {update.isError && (
        <DSAlert severity="error">
          {errorMessage(update.error, "Failed to save the feature.")}
        </DSAlert>
      )}
      {dirty && (
        <DSBox sx={{ display: "flex", justifyContent: "flex-end" }}>
          <DSPrimaryButton
            disabled={!title.trim() || update.isPending}
            onClick={() =>
              update.mutate({
                id: props.feature.id,
                input: {
                  title: title.trim(),
                  description: description.trim() || null,
                  status,
                },
              })
            }
          >
            Save changes
          </DSPrimaryButton>
        </DSBox>
      )}
    </DSBox>
  );
};

const FeatureDetailModal = (props: {
  featureId: string | null;
  onClose: () => void;
}) => {
  const { data: features = [] } = useProductFeaturesQuery();
  const feature = features.find((f) => f.id === props.featureId);
  const { data: allRequests, isLoading: requestsLoading } =
    useFeatureRequestsQuery();
  const requests = useMemo(
    () => (allRequests ?? []).filter((r) => r.featureId === props.featureId),
    [allRequests, props.featureId],
  );

  const deleteFeature = useDeleteFeatureMutation();
  const mergeFeature = useMergeFeatureMutation();
  const undeploy = useUndeployFeatureMutation();

  const [addOpen, setAddOpen] = useState(false);
  const [deployOpen, setDeployOpen] = useState(false);
  const [undeployOpen, setUndeployOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  if (!feature) return null;
  const statusMeta = getFeatureStatusMeta(feature.status);
  const actionError = deleteFeature.error ?? undeploy.error;

  return (
    <DSDialog open onClose={props.onClose} fullWidth maxWidth="lg">
      <DSDialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        {feature.title}
        <DSChip
          label={statusMeta.label}
          color={statusMeta.color}
          size="small"
          variant="outlined"
        />
      </DSDialogTitle>
      <DSDialogContent>
        <DSBox sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <FeatureEditor key={feature.id} feature={feature} />

          <DSBox
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              p: 2,
              borderRadius: 2,
              bgcolor: "action.hover",
            }}
          >
            {feature.deployedVersion ? (
              <DSTypography>
                Deployed in <strong>{feature.deployedVersion}</strong> on{" "}
                {formatDate(feature.deployedAt)}.{" "}
                {feature.potentiallyResolvedCount > 0
                  ? `${feature.potentiallyResolvedCount} request(s) waiting for review below.`
                  : ""}
              </DSTypography>
            ) : (
              <DSTypography sx={{ color: "text.secondary" }}>
                Not deployed yet. Marking it deployed flags its open requests as
                potentially resolved.
              </DSTypography>
            )}
            <DSBox sx={{ display: "flex", gap: 1 }}>
              {feature.deployedVersion && (
                <DSSecondaryButton
                  disabled={undeploy.isPending}
                  onClick={() => setUndeployOpen(true)}
                >
                  Undo deploy
                </DSSecondaryButton>
              )}
              <DSPrimaryButton onClick={() => setDeployOpen(true)}>
                {feature.deployedVersion ? "Change version" : "Mark deployed"}
              </DSPrimaryButton>
            </DSBox>
          </DSBox>

          <DSBox
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
            }}
          >
            <DSTypography sx={{ color: "text.secondary", fontSize: 14 }}>
              {feature.customerCount} customer
              {feature.customerCount === 1 ? "" : "s"} · {feature.openCount}{" "}
              open · {feature.potentiallyResolvedCount} to review ·{" "}
              {feature.fulfilledCount} fulfilled
            </DSTypography>
            <DSBox sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <DSSecondaryButton onClick={() => setAddOpen(true)}>
                Add request
              </DSSecondaryButton>
              <DSSecondaryButton onClick={() => setMergeOpen(true)}>
                Merge into…
              </DSSecondaryButton>
              {feature.requestCount === 0 && (
                <DSErrorButton onClick={() => setDeleteOpen(true)}>
                  Delete feature
                </DSErrorButton>
              )}
            </DSBox>
          </DSBox>

          {actionError && (
            <DSAlert severity="error">
              {errorMessage(actionError, "Something went wrong.")}
            </DSAlert>
          )}

          {requestsLoading ? (
            <DSBox sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <DSCircularProgress size={24} />
            </DSBox>
          ) : (
            <RequestsTable
              requests={requests}
              features={features}
              emptyText="No requests yet."
            />
          )}
        </DSBox>
      </DSDialogContent>
      <DSDialogActions>
        <DSSecondaryButton onClick={props.onClose}>Close</DSSecondaryButton>
      </DSDialogActions>

      <AddRequestModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        defaultFeature={feature}
      />
      <DeployFeatureDialog
        open={deployOpen}
        feature={feature}
        onClose={() => setDeployOpen(false)}
      />
      <ConfirmationModal
        open={undeployOpen}
        title="Undo deploy"
        message={`Clear the deployed version of "${feature.title}"? Requests flagged as potentially resolved go back to open; fulfilled requests are kept.`}
        onClose={() => setUndeployOpen(false)}
        onConfirm={() => {
          setUndeployOpen(false);
          undeploy.mutate(feature.id);
        }}
      />
      <PickFeatureDialog
        open={mergeOpen}
        title="Merge feature"
        description={
          <>
            All {feature.requestCount} request(s) of &quot;{feature.title}
            &quot; will move to the feature you pick, and &quot;
            {feature.title}&quot; will be deleted. Fulfilled requests stay
            fulfilled; the rest follow the target feature&apos;s deploy state.
          </>
        }
        confirmLabel="Merge"
        features={features}
        excludeId={feature.id}
        isPending={mergeFeature.isPending}
        error={
          mergeFeature.isError
            ? errorMessage(mergeFeature.error, "Failed to merge.")
            : null
        }
        onClose={() => {
          setMergeOpen(false);
          mergeFeature.reset();
        }}
        onConfirm={(into) =>
          mergeFeature.mutate(
            { id: feature.id, intoId: into.id },
            { onSuccess: props.onClose },
          )
        }
      />
      <ConfirmationModal
        open={deleteOpen}
        title="Delete feature"
        message={`Delete "${feature.title}"?`}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => {
          setDeleteOpen(false);
          deleteFeature.mutate(feature.id, { onSuccess: props.onClose });
        }}
      />
    </DSDialog>
  );
};

export default FeatureDetailModal;
